"use client";

import { useSupabaseClient } from "@/components/SupabaseProvider";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { VideoModel } from "@ziggapp/klap-core/video";
import { useCallback, useMemo } from "react";
import { deserializeFolder } from "../models/folder_model";
import { klap } from "../utils/klap";

export type VideoResource = VideoModel;

export const useResources = (userId: string) => {
  const supabaseClient = useSupabaseClient();
  const resultsPerPage = 9;

  const getTopLevelVideos = async (userId: string, page: number) => {
    const { data, error } = await supabaseClient
      .from("videos_v2")
      .select("*")
      .eq("author_id", userId)
      .is("folder_id", null)
      .order("created_at", { ascending: false })
      .range(page * resultsPerPage, (page + 1) * resultsPerPage);
    if (error) throw error;

    return data.map(klap.video.fromRow);
  };

  const getFolders = async (userId: string, page: number) => {
    const { data, error } = await supabaseClient
      .from("folders")
      .select("*")
      .eq("author_id", userId)
      .order("created_at", { ascending: false })
      .range(page * resultsPerPage, (page + 1) * resultsPerPage);

    if (error) throw error;
    return data.map(deserializeFolder);
  };

  const fetchResources = useCallback(
    async (page: number) => {
      const [folders, videos] = await Promise.all([
        getFolders(userId, page),
        getTopLevelVideos(userId, page),
      ]).catch((error) => {
        console.error("Error fetching resources:", error);
        return [[], []];
      });

      return [...folders, ...videos].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    },
    [userId],
  );

  const query = useInfiniteQuery({
    enabled: !!userId,
    queryKey: ["resources", userId],
    queryFn: ({ pageParam = 0 }) => fetchResources(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.length || lastPage.length < resultsPerPage)
        return undefined;
      return pages.length;
    },
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    const seen = new Set<string>();
    return query.data.pages.flat().filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  }, [query.data]);

  return {
    data,
    isLoading: query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
  };
};
